import { makeAutoObservable, toJS } from 'mobx'
import {
  AvFactory,
  AvClientGroupFactory,
  CarrierFactory,
  ContactFactory,
  CoverageFactory,
  LeadSourceFactory,
  PointOverrideFactory,
  UsersCarrierContractFactory,
} from './../../../shared/factories'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from './../../../shared/services/User.service'
import AgentWritingCodeModal from './../components/AgentWritingCodeModal/AgentWritingCodeModal.component'
import { create } from 'react-modal-promise'
import moment from 'moment'
import submitSound from './../assets/submit-av-play-sound.mp3'
import { TrackJS } from 'trackjs'
import { CrmStore } from './../../../components/crm'
import MaxAvDateStore from './max-av-date.store'

const AgentWritingCodeModalPromise = create(AgentWritingCodeModal),
  CHECK_FORM_VALIDITY = true,
  CHECK_WRITING_CODES = false,
  // The server's rejection names this status; shown as-is instead of the generic fields message.
  TRANSFER_W_PTS_LABEL = '"Transfer (w Pts)"',
  CLIENT_FIELD_NAMES = {
    client_group_id: 'Client Group',
    av_client_fname: 'Client First Name',
    av_client_lname: 'Client Last Name',
    av_client_email: 'Client Email',
    av_client_phone: 'Client Phone',
    av_state: 'Client State',
  },
  SUBMISSION_FIELD_NAMES = {
    av_date: 'Date to Carrier (AV Date)',
    leadsource_id: 'Lead Source',
    selling_agent_id: 'Agent of Record',
  },
  POLICY_FIELD_NAMES = {
    carrier_id: 'Carrier',
    coverage_id: 'Coverage',
    av_product: 'Product',
    av_term_length: 'Term Length',
    av_status: 'Application Status',
    av_appid: 'Policy # or D.O.B.',
    av_fee: 'Application Fee',
    av_premium: 'Premium',
    av_eff_date: 'Effective Date',
    av_comments: 'Comments',
  }

function getPreviousMonday(dateInput) {
  let date

  if (!dateInput) date = new Date()
  else if (dateInput instanceof Date && !isNaN(dateInput)) date = new Date(dateInput)
  else if (typeof dateInput === 'string') {
    // Ensure date string is parsed as local date, not UTC
    const parts = dateInput.split('T')[0].split('-'),
      year = parseInt(parts[0], 10),
      month = parseInt(parts[1], 10) - 1, // 0-based month
      day = parseInt(parts[2], 10)
    date = new Date(year, month, day)
  }

  // At this point, `date` is guaranteed to be a local Date object
  const monday = new Date(date)
  let dayOfWeek = monday.getDay()

  // Adjust Sunday (0) to 7 so Monday stays start of the week
  if (dayOfWeek === 0) dayOfWeek = 7

  // Subtract (day - 1) to get to Monday
  monday.setDate(monday.getDate() - (dayOfWeek - 1))
  monday.setHours(0, 0, 0, 0)

  return monday
}

const determineMinimumAVDate = avDate => {
  return avDate && getPreviousMonday(avDate)
}

const getClientFields = () => Object.keys(CLIENT_FIELD_NAMES)

const getSubmissionFields = () => Object.keys(SUBMISSION_FIELD_NAMES)

const getPolicyFields = () => Object.keys(POLICY_FIELD_NAMES)

const playChaChing = () => {
  try {
    new Audio(submitSound).play()
    return new Promise((resolve, reject) => setTimeout(resolve, 2500))
  } catch (ex) {
    console.error(ex)
    return Promise.resolve()
  }
}

const mergeErrors = errors => {
  errors = Array.isArray(errors) ? Object.values(errors) : []

  if (errors.length === 1) return errors[0]

  if (errors.length === 2) return `${errors[0]} and ${errors[1]}`

  if (errors.length > 2) {
    let e = errors.pop()
    return `${errors.join(', ')} and ${e}`
  }

  return ''
}

const recordTrackJs = (msg, payload) => {
  let user_id = UserProfileService.getUserId()
  if (window.TrackJS) {
    TrackJS.console.debug(`[SALES-TRACKER] User ${user_id}: Sales Tracker Log: ${msg}`, payload)
  } else {
    console.error(`[SALES-TRACKER] User ${user_id}: Sales Tracker Log: ${msg}`)
    console.error(payload)
  }
}

const compileErrorMessages = errors => {
  const defaultMsg = 'Errors exist.  Please check all Client & Policy form fields and try again.'
  let clentErrorMsgs = [],
    submissionErrorMsgs = [],
    policyErrorMsgs = {},
    policyErrors

  Object.keys(errors).forEach(policyIdx => {
    const invalid = Object.keys(errors[policyIdx]).filter(key => errors[policyIdx][key] === false)
    clentErrorMsgs = clentErrorMsgs.concat(invalid.filter(key => getClientFields().includes(key)))
    submissionErrorMsgs = submissionErrorMsgs.concat(invalid.filter(key => getSubmissionFields().includes(key)))
    policyErrors = invalid.filter(key => getPolicyFields().includes(key))
    if (policyErrors.length > 0) policyErrorMsgs[policyIdx] = policyErrors
  })

  if (errors.length) recordTrackJs('compileErrorMessages: First breakpoint ', { errors })

  clentErrorMsgs = mergeErrors(
    clentErrorMsgs.filter((val, idx, self) => self.indexOf(val) === idx).map(field => CLIENT_FIELD_NAMES[field])
  )
  clentErrorMsgs = clentErrorMsgs.length
    ? `Please complete or correct the following fields in 'Client Details' section: ${clentErrorMsgs}`
    : null

  submissionErrorMsgs = mergeErrors(
    submissionErrorMsgs
      .filter((val, idx, self) => self.indexOf(val) === idx)
      .map(field => SUBMISSION_FIELD_NAMES[field])
  )
  submissionErrorMsgs = submissionErrorMsgs.length
    ? `Please complete or correct the following fields in 'Submission Details' section: ${submissionErrorMsgs}`
    : null

  policyErrorMsgs = Object.keys(policyErrorMsgs)
    .map(policyIdx => {
      let errMsg = mergeErrors(policyErrorMsgs[policyIdx].map(field => POLICY_FIELD_NAMES[field]))
      return errMsg.length
        ? `Please complete or correct the following fields on policy #${parseInt(policyIdx) + 1}: ${errMsg}.`
        : null
    })
    .filter(n => !!n)

  const errMsgs = [clentErrorMsgs, submissionErrorMsgs].concat(policyErrorMsgs).filter(n => !!n)

  if (errMsgs.length) recordTrackJs('compileErrorMessages: Second breakpoint ', { errMsgs })
  else recordTrackJs('compileErrorMessages: Third breakpoint ', { defaultMsg })

  return (errMsgs.length > 0 && errMsgs) || [defaultMsg]
}

class SalesTrackerStore {
  isFetching = false
  isSubmitting = false
  isDeleting = false
  showErrors = false
  responseErrors = false
  transferErrMsg = null
  errMsg = null
  errMsgs = null

  contactId = null
  Contact = null
  Policy = AvFactory.create({ user_id: UserProfileService.getUserId() })
  Policies = []
  Coverages = []
  Carriers = []
  agentContracts = {}
  CoveragesByCarrier = {}
  LeadSources = []
  PointOverrides = []
  PointOverrideFields = {}
  ClientGroups = []
  AOR = { sellingAgentList: [], sellingAgentId: null, avSellingAgent: '' }
  clientType = 'individual'
  policyErrors = {}
  isPreviousPolicy = false
  previousPolicyHasEffDate = false

  constructor() {
    makeAutoObservable(this)
  }

  reset = () => {
    this.Policy = {}
    this.Policies = []

    const Policy = AvFactory.create({
      user_id: UserProfileService.getUserId(),
      av_appid: null,
      contact_id: null,
      client_group_id: null,
      av_client_fname: null,
      av_client_lname: null,
      av_client_email: null,
      av_client_phone: null,
      av_fee: null,
      av_premium: null,
      av_product: null,
      av_state: null,
      aca_lives: 0,
      av_comments: null,
      av_date: moment().local().format('YYYY-MM-DD'),
      av_eff_date: null,
      av_term_length: null,
      pts_override_id: null,
      pts_override_val: null,
      comm_paid: null,
      comm_is_chrg_bk: false,
    })
    this.Policy = Policy
    this.Policies = [Policy]
    this.PointOverrides = []
    this.PointOverrideFields = {}

    this.policyErrors = {}
    this.AOR = {
      sellingAgentList: [],
      sellingAgentId: null,
      avSellingAgent: '',
    }
    this.agentContracts = {}
    this.contactId = null
    this.Contact = null
    this.ClientGroups = []
    this.showErrors = false
    this.responseErrors = false
    this.errMsg = null
    this.errMsgs = null
    this.isPreviousPolicy = false
    this.previousPolicyHasEffDate = false

    MaxAvDateStore.setPolicy(this.Policy)

    this.determineSellingAgents()
      .then(AOR => this.setSellingAgents(AOR))
      .finally(() => this.fetchAgentCarrierContracts())
  }

  setCrmContact = contactId => {
    const newContactId = `${contactId || ''}`,
      currContactId = `${this.contactId || ''}`

    // Determine if the contact ID has changed.
    if (newContactId !== currContactId) {
      // Contact has changed.
      if (contactId) {
        this.contactId = contactId
      }

      // Contact has been removed.
      else {
        this.contactId = this.Contact = null
      }

      if (this.contactId) {
        // fetch client.
        ContactFactory.findById(this.contactId).then(Contact => {
          if ((this.Contact = Contact)) {
            if (!this.Policy.get('av_client_fname')) this.Policy.set('av_client_fname', this.Contact.get('first_name'))
            if (!this.Policy.get('av_client_lname')) this.Policy.set('av_client_lname', this.Contact.get('last_name'))
            if (!this.Policy.get('av_client_email')) this.Policy.set('av_client_email', this.Contact.get('email'))
            if (!this.Policy.get('av_client_phone')) this.Policy.set('av_client_phone', this.Contact.get('phone'))
            if (!this.Policy.get('av_state')) this.Policy.set('av_state', this.Contact.get('state'))
          }
        })
      }

      return
    }
  }

  fetchById = async avId => {
    this.isFetching = true

    if (this.avId && avId) {
      if (parseInt(this.avId) !== parseInt(avId)) this.reset()
    }

    this.avId = avId

    if (!avId || isNaN(avId)) {
      this.reset()

      /**/
      // Testing Content
      //
      // const payload = {
      //  av_appid        :   "19/18/24",
      //  av_carrier      :   "Allstate (National General)",
      //  av_client       :   "jane faksf",
      //  av_client_email :   "adf!gmail@gmail.com",
      //  av_client_phone :   "234234i3u44sdfsdfasdf",
      //  av_coverage     :   "Defined Benefit Health Plans",
      //  // av_date          :   "2023-03-01",
      //   av_date         :   "",
      //  av_fee          :   342,
      //  av_leadsource   :   "Gametime Leads",
      //  av_points       :   0,
      //  av_premium      :   372.22,
      //  av_product      :   "Classic ",
      //  av_state        :   "FL",
      //  av_status       :   "Withdrawn",
      //  av_term_length  :   "7",
      //  av_type         :   null,
      //  av_user         :   "Lews Rudkin",
      //  carrier_id      :   790,
      //  coverage_id     :   83,
      //  leadsource_id   :   723,
      //  aca_lives           :   0,
      //  r_partner_id    :   null,
      //  sub_agent_id    :   null,
      //  user_id         :   9367,
      // };
      // Object.keys(payload).forEach(field => this.Policy.set(field, payload[field]))

      // console.log('save:: ',await Policy.save());
      // console.log('id: ',Policy.id());
      // console.log('reload:: ',await Policy.reload());
      // console.log('group id: ',Policy.get('av_group_id'),Policy.get('av_date'),Policy.get('av_status'));
      /**/

      this.isFetching = false
      return
    }

    let Policy = null,
      Policies = []
    try {
      Policies = await AvFactory.search({
        search: { av_id: avId },
        pagination: false,
        order_by: { av_id: 'ASC' },
      })
    } catch (ex) {
      Policies = []
      console.error(`Failed to fetch AV Policies ${ex}`)
    }

    if (Policies.length) {
      Policy = Policies.shift()
      if (Policy) Policies = [Policy].concat(Policies)
    }

    // Set Carriers by Coverage.
    await Promise.all(Policies.map(Policy => this.fetchCoveragesByCarrier(Policy.get('carrier_id'))))

    // A different AV (or the new-sale form) was requested while this one loaded; drop the stale result.
    if (parseInt(this.avId) !== parseInt(avId)) return

    // Init CRM Contact, if applied.
    if ((this.contactId = Policy.get('contact_id') || null)) CrmStore.fetchContactById(this.contactId)

    this.Policy = Policy
    this.clientType = Policy.getClientType()
    this.isPreviousPolicy = true
    if (Policy && Policy?.get('av_eff_date')) this.previousPolicyHasEffDate = true
    this.Policies = Policies
    if (!Policy.isNew()) {
      let idx = 0
      this.AOR.sellingAgentId = Policy.get('user_id')
      Policies.forEach(Policy => {
        this.setPointOverrideOptions(
          this.getCoverage(Policy.get('coverage_id')),
          this.getCarrier(Policy.get('carrier_id')),
          idx
        )
        idx++
      })
    }

    MaxAvDateStore.setPolicy(this.Policy)

    this.isFetching = false

    return Policy
  }

  fetchClientGroups = async userId => {
    if (!userId) {
      this.ClientGroups = []
      return this.ClientGroups
    }

    try {
      this.ClientGroups = ((await AvClientGroupFactory.findAllByUserId(userId)) || []).sort((A, B) =>
        `${A.get('group_name')}`.localeCompare(`${B.get('group_name')}`)
      )
    } catch (ex) {
      this.ClientGroups = []
      console.error(`Failed to fetch Client Groups ${ex}`)
    }

    return this.ClientGroups
  }

  fetchCoverages = async () => {
    try {
      this.Coverages = (await CoverageFactory.findAll()).sort((A, B) =>
        `${A.get('coverage_name')}`.localeCompare(`${B.get('coverage_name')}`)
      )
    } catch (ex) {
      this.Coverages = []
      console.error(`Failed to fetch Coverages ${ex}`)
    }

    return this.Coverages
  }

  fetchCarriers = async () => {
    try {
      this.Carriers = (await CarrierFactory.findAll()).sort((A, B) =>
        `${A.get('c_name')}`.localeCompare(`${B.get('c_name')}`)
      )
    } catch (ex) {
      this.Carriers = []
      console.error(`Failed to fetch Carriers ${ex}`)
    }

    return this.Carriers
  }

  fetchPointOverrides = async () => {
    this.PointOverrides = []
    // Note: Do NOT reset PointOverrideFields here - it gets populated by
    // setPointOverrideOptions and resetting it causes a race condition
    // where the fields disappear after componentDidUpdate calls this method.

    let PointOverrides
    try {
      PointOverrides = await PointOverrideFactory.findAll()
    } catch (ex) {
      PointOverrides = []
      console.error(`Failed to fetch point overrides. ${ex}`)
    }

    return (this.PointOverrides = PointOverrides.filter(P => !P.get('deleted_at')))
  }

  fetchLeadSources = async () => {
    try {
      this.LeadSources = (await LeadSourceFactory.findAll()).sort((A, B) =>
        `${A.get('ls_name')}`.localeCompare(`${B.get('ls_name')}`)
      )
    } catch (ex) {
      this.LeadSources = []
      console.error(`Failed to fetch Lead Sources ${ex}`)
    }

    return this.LeadSources
  }

  fetchCoveragesByCarrier = async carrierId => {
    if (
      this.CoveragesByCarrier[carrierId] &&
      Array.isArray(this.CoveragesByCarrier[carrierId]) &&
      this.CoveragesByCarrier[carrierId].length > 0
    )
      return true

    let Coverages
    try {
      Coverages = await CoverageFactory.search({
        search: { carrier_id: carrierId },
        pagination: false,
      })
    } catch (ex) {
      console.error(`Failed to fetch Carriers by Coverage ID. ${ex}`)
    }

    if (Coverages && Array.isArray(Coverages)) this.CoveragesByCarrier[carrierId] = Coverages
    return
  }

  fetchAgentCarrierContracts = async () => {
    const userIds = this.AOR.sellingAgentList.map(a => a.value)
    let agentContracts = {}

    if (userIds) {
      ;(
        await UsersCarrierContractFactory.search({
          search: { user_id: userIds },
          pagination: false,
        })
      )
        .filter(Contract => !['expired'].includes(Contract.get('disposition')))
        .forEach(Contract => {
          let userId = Contract.get('user_id')
          if (userId && !isNaN(userId)) {
            if (!agentContracts.hasOwnProperty(userId)) agentContracts[userId] = []
            agentContracts[userId].push(parseInt(Contract.get('carrier_id')))
          }
        })
    }

    this.agentContracts = agentContracts
  }

  setPointOverrideOptions = (Coverage, Carrier, idx) => {
    // Ensure that this.PointOverrideFields[idx] is initialized
    if (!this.PointOverrideFields[idx]) {
      this.PointOverrideFields[idx] = {}
    }

    const POverrides = this.PointOverrides.filter(
        P =>
          (P.get('coverage_id') && `${P.get('coverage_id')}` === `${Coverage?.id()}`) ||
          (P.get('carrier_id') && `${P.get('carrier_id')}` === `${Carrier?.id()}`)
      ),
      fields = {}

    POverrides.forEach(P => {
      if (!fields.hasOwnProperty(P.get('field'))) fields[P.get('field')] = []
      fields[P.get('field')].push(P)
    })

    Object.keys(fields).forEach(acField => (this.PointOverrideFields[idx][acField] = fields[acField]))
  }

  getCarrier = carrierId =>
    toJS(this.Carriers.filter(Carrier => parseInt(Carrier.id()) === parseInt(carrierId)).shift())

  getCoverage = coverageId =>
    toJS(this.Coverages.filter(Coverage => parseInt(Coverage.id()) === parseInt(coverageId)).shift())

  getSellingAgent = async aId => {
    let A = Array.isArray(this.AOR.sellingAgentList)
      ? this.AOR.sellingAgentList.filter(u => parseInt(u.value) === parseInt(aId)).shift()
      : null
    if (A) return A

    let agentDetails = await UserService.getUserDetails(aId)
    if (!agentDetails) return

    return [
      {
        text: [agentDetails.u_fname, agentDetails.u_lname].join(' ').trim(),
        value: aId,
      },
    ]
  }

  getAvSellingAgent = async aId => {
    try {
      return (await this.getSellingAgent(aId)).shift().text
    } catch (ex) {
      return undefined
    }
  }

  setValidity = (policyIdx, field, isValid) => {
    if (!this.policyErrors[policyIdx]) this.policyErrors[policyIdx] = {}

    this.policyErrors[policyIdx][field] = isValid
  }

  unsetPolicyValidity = policyIdx => {
    if (this.policyErrors[policyIdx]) {
      // 1. Delete the property
      delete this.policyErrors[policyIdx]

      // 2. Re-index the remaining properties
      const entries = Object.entries(this.policyErrors)
      this.policyErrors = {}

      entries.forEach(([_, value], newIndex) => {
        this.policyErrors[newIndex] = value
      })
    }
  }

  checkValidity = () => {
    if (this.policyErrors) {
      // Check if any policy error contains a false value
      const errorDetails = Object.entries(this.policyErrors)
        .filter(([policyIdx, policyErrs]) => Object.values(policyErrs).includes(false))
        .map(([policyIdx, policyErrs]) => {
          const fieldsWithErrors = Object.entries(policyErrs)
            .filter(([, isValid]) => !isValid)
            .map(([field]) => field)
          return { policyIdx, fields: fieldsWithErrors }
        })

      if (errorDetails.length > 0) {
        errorDetails.forEach(({ policyIdx, fields }) => {
          console.log(`Policy Index: ${policyIdx}, Fields with errors: ${fields.join(', ')}`)
        })
        return false
      }
    }

    return true
  }

  verifyWritingCodes = async () => {
    // Returns false if agent needs to supply a writing number.
    const verifyWritingCode = async (Policy, idx) => {
      try {
        if (Policy.changed?.hasOwnProperty('carrier_id')) {
          const Contract = await Policy.verifyWritingCode({
            user_id: UserProfileService.getUserId(),
            carrier_id: Policy.get('carrier_id'),
          })

          if (!!Contract?.error === true && Contract?.status === 400) {
            // We need writing code
            return await AgentWritingCodeModalPromise({
              carrier_id: Policy.get('carrier_id'),
              av_carrier: Policy.get('av_carrier'),
              isOpen: true,
            })
          } else if (!!Contract?.error === true && Contract?.status === 500) {
            return { error: true, message: Contract.message, status: 500 }
          }

          if (Contract) return { error: false }
        }

        return { error: false }
      } catch (ex) {
        console.error(`Reject Modal ${ex}`)
        return { error: true, message: `Reject Modal ${ex}` }
      }
    }

    let results = []
    for (let Policy of this.Policies) {
      const result = await verifyWritingCode(Policy)
      if (result.error === true) {
        // Return false along with the error message
        return {
          success: false,
          message: result.message,
          status: result?.status ?? '',
        }
      }
      results.push(result)
    }

    return results.length > 0 && results.filter(n => n === false).length === 0
  }

  setClientType = clientType => {
    if (clientType === 'individual' && this.Policy) this.Policy.set('client_group_id', null)
    this.clientType = clientType
  }

  setClientGroup = ClientGroup => {
    this.Policy.set('client_group_id', (ClientGroup && ClientGroup.id()) || null)
    return ClientGroup || null
  }

  determineSellingAgents = async () => {
    let userDetails = UserProfileService.getUserDetails(),
      uplineId =
        userDetails && typeof userDetails === 'object' && userDetails.u_upline_id
          ? parseInt(userDetails.u_upline_id, 10)
          : false,
      uplineDetails = uplineId ? await UserService.getUserDetails(uplineId) : null,
      sellingAgentList = [
        {
          text: [userDetails.u_fname, userDetails.u_lname].join(' ').trim(),
          value: `${userDetails.id}`,
        },
      ],
      sellingAgentId = UserProfileService.getUserId(),
      avSellingAgent = null

    if (UserProfileService.isA(['career-agent', 'division-admin', 'region-admin', 'district-admin']))
      sellingAgentList.push({
        text: [uplineDetails.u_fname, uplineDetails.u_lname].join(' ').trim(),
        value: `${uplineDetails.id}`,
      })

    if (Array.isArray(sellingAgentList) && sellingAgentList.filter(s => s).length) {
      if (this.AOR.sellingAgentId && !isNaN(this.AOR.sellingAgentId)) {
        if (sellingAgentList.filter(item => `${item.value}` === `${this.AOR.sellingAgentId}`).length > 0)
          sellingAgentId = this.AOR.sellingAgentId
      }

      sellingAgentId = sellingAgentId ? sellingAgentId : sellingAgentList[0].value
      if (sellingAgentId) avSellingAgent = await this.getAvSellingAgent(sellingAgentId)
    }

    return { sellingAgentList, sellingAgentId, avSellingAgent }
  }

  setSellingAgents = AOR => {
    this.AOR = AOR
  }

  saveAncillaryPolicies = async () => {
    // Make sure all the attached policies have matching client/agent fields.
    const Policies = this.Policies.map(Policy => {
      Policy.set('contact_id', this.contactId)
      ;[
        'client_group_id',
        'av_group_id',
        'av_client_fname',
        'av_client_lname',
        'av_client_email',
        'av_client_phone',
        'av_state',
        'av_date',
        'av_leadsource',
        'leadsource_id',
        'r_partner_id',
        'selling_agent_id',
      ].forEach(field => Policy.set(field, this.Policy.get(field)))
      return Policy
    })

    // Attempt to save the ancillary policies.
    try {
      for (const Policy of Policies) {
        if (Policy.get('pts_override_id')) {
          const ptsOverrideId = parseInt(Policy.get('pts_override_id'))
          const policyCarrierId = parseInt(Policy.get('carrier_id'))
          const policyCoverageId = parseInt(Policy.get('coverage_id'))

          const override = this.PointOverrides.find(po => parseInt(po.id()) === ptsOverrideId)

          if (override) {
            const overrideCarrierId = parseInt(override.get('carrier_id'))
            const overrideCoverageId = parseInt(override.get('coverage_id'))

            // If does not match
            if (overrideCarrierId !== policyCarrierId || overrideCoverageId !== policyCoverageId) {
              // Umnset it
              Policy.set('pts_override_id', null)
              Policy.set('pts_override_val', null)
            }
          } else {
            // Else unset it
            Policy.set('pts_override_id', null)
            Policy.set('pts_override_val', null)
          }
        }

        await Policy.save() // Wait for each save before proceeding to the next one
      }
      this.Policies = Policies
      return true
    } catch (ex) {
      if (`${ex}`.includes(TRANSFER_W_PTS_LABEL)) this.transferErrMsg = `${ex}`
      return false
    }
  }

  saveMasterPolicy = async () => {
    let avId = this.Policy.id(),
      isNew = this.Policy.isNew(),
      responseErrors = null,
      success = false,
      hasChanged = toJS(this.Policy.changed)

    // determine if the model has any changes.
    hasChanged = !(!hasChanged || typeof hasChanged !== 'object' || !Object.keys(hasChanged).length)

    this.Policy.set('selling_agent_id', this.AOR.sellingAgentId)
    this.Policy.set('contact_id', this.contactId)

    if (this.Policy.get('pts_override_id')) {
      const ptsOverrideId = parseInt(this.Policy.get('pts_override_id'))
      const policyCarrierId = parseInt(this.Policy.get('carrier_id'))
      const policyCoverageId = parseInt(this.Policy.get('coverage_id'))

      const override = this.PointOverrides.find(po => parseInt(po.id()) === ptsOverrideId)

      if (override) {
        const overrideCarrierId = parseInt(override.get('carrier_id'))
        const overrideCoverageId = parseInt(override.get('coverage_id'))

        if (overrideCarrierId !== policyCarrierId || overrideCoverageId !== policyCoverageId) {
          this.Policy.set('pts_override_id', null)
          this.Policy.set('pts_override_val', null)
        }
      } else {
        this.Policy.set('pts_override_id', null)
        this.Policy.set('pts_override_val', null)
      }
    }

    try {
      if (isNew || hasChanged) await this.Policy.save()
      if (isNew) await this.Policy.reload()
      if (isNew) avId = this.Policy.id()
      success = true
    } catch (ex) {
      responseErrors = { 0: `${ex}` }
      if (`${ex}`.includes(TRANSFER_W_PTS_LABEL)) this.transferErrMsg = `${ex}`
    }

    if (this.Policy.failed) responseErrors = { 0: toJS(this.Policy.failed) }

    if (responseErrors && typeof responseErrors === 'object' && Object.values(responseErrors).length) {
      responseErrors[0] = Object.values(responseErrors)[0]
      if (responseErrors[0] && typeof responseErrors[0] === 'object')
        Object.keys(responseErrors[0]).forEach(key => {
          if (!responseErrors[0][key] || !responseErrors[0][key].length) {
            delete responseErrors[0][key]
            return
          }

          responseErrors[0][key] = false
        })
    }

    this.responseErrors = responseErrors

    if (!isNew || (isNaN(avId) && avId)) this.avId = avId

    return success
  }

  onSuccess = async isNew => {
    // Play the cash-register sound, indicating vectory!
    await playChaChing()

    if (isNew) window.location.href = `/submit-sales/${this.Policy.id()}/edit`
  }

  onError = msgs => {
    if (!msgs) {
      let policyErrors = toJS(this.policyErrors),
        responseErrors = toJS(this.responseErrors),
        policyIdxs = (responseErrors && Object.keys(responseErrors)) || []

      policyIdxs.forEach(
        idx =>
          (policyErrors[idx] = {
            ...(policyErrors[idx] || {}),
            ...responseErrors[idx],
          })
      )
      msgs = compileErrorMessages(toJS((this.policyErrors = policyErrors)))
    }

    this.errMsgs = Array.isArray(msgs) ? msgs : [msgs]
    this.errMsg = this.errMsgs.join(`<br />`)
    this.showErrors = true
  }

  submit = async () => {
    let results = false,
      isNew = this.Policy?.isNew()
    this.errMsg = this.errMsgs = this.transferErrMsg = null

    // 1. Check Policy validity locally.
    if (CHECK_FORM_VALIDITY) {
      if (!this.checkValidity()) {
        recordTrackJs('submit(): Error at CHECK_FORM_VALIDITY', {
          policyErrors: this.policyErrors,
        })
        return this.onError()
      }
    }

    // 2. Activate the submitting flag to disable to submit button.
    this.isSubmitting = true

    // 3. Check Agent for writing codes.
    if (CHECK_WRITING_CODES) {
      let validateWritingCode = await this.verifyWritingCodes()
      if (
        (validateWritingCode.success === false && validateWritingCode.status !== 500) ||
        validateWritingCode === false
      ) {
        this.isSubmitting = false
        // Handle the error when the writing code validation fails
        return this.onError([
          'You must have an agent writing code for all carriers. Please update any missing & required agent writing codes, then try again.',
        ])
      } else if (validateWritingCode.success === false && parseInt(validateWritingCode.status) === 500) {
        this.isSubmitting = false
        return this.onError(['Something went wrong, please try again.'])
      }
    }

    // 4. Save Policies.
    try {
      if (await this.saveMasterPolicy()) {
        this.Policy.set('selling_agent_id', this.AOR.sellingAgentId)
        results = await this.saveAncillaryPolicies()
      }
    } catch (error) {
      recordTrackJs('submit(): Policy Saved Failed', { error })
    }

    // 5. Handle submission outcome.
    if (!!results) {
      this.onSuccess(isNew)
    } else {
      // Error at Handle submission outcome
      recordTrackJs('submit(): Error at Handle submission outcome', { results })
      this.onError(this.transferErrMsg || undefined)
    }

    // 6. Deactivate the submitting flag.
    this.isSubmitting = false

    return !!results
  }

  startPolicy = () =>
    this.Policies.push(
      AvFactory.create({
        user_id: UserProfileService.getUserId(),
        av_appid: null,
        client_group_id: null,
        av_client_fname: null,
        av_client_lname: null,
        av_client_email: null,
        av_client_phone: null,
        av_fee: null,
        av_premium: null,
        av_product: null,
        av_state: null,
        aca_lives: 0,
        av_comments: null,
        av_eff_date: null,
        av_term_length: null,
        comm_paid: null,
        comm_is_chrg_bk: false,
      })
    )

  deleteAllPolicies = async () => {
    this.isDeleting = true

    await Promise.all(this.Policies.map(async Policy => Policy.delete()))

    this.isDeleting = false

    this.reset()

    window.location.href = `/submit-sales`
  }

  isAdmin = () => UserProfileService.isA(['system-admin', 'agency-owner', 'internal-admin'], true)
}

export default new SalesTrackerStore()
